import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {FITHGameConfig} from "../FITHGameConfig";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {TimelineLite, TweenLite} from "gsap";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {FITHReelSymbol} from "./FITHReelSymbol";
import {FITHGameModuleConfig} from "../FITHGameModuleConfig";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {FITHSoundManager} from "../sound/FITHSoundManager";

export class FITHSymbolWinAnimations extends BaseView {
    private readonly _layer: ScalableLayer;

    private _commonWinAnim: TimelineSprite[][] = [];
    private _symWinAnim: TimelineSprite[][] = [];
    private _extraLSymWinAnim: TimelineSprite[][] = [];
    private _extraSymbol: PIXI.Sprite[][] = [];
    private _wildExplodeDSym: TimelineSprite[][] = [];
    private _extraLayerMedLargeWin: TimelineSprite[][] = [];
    private _wildExplodeSoundPlayed: boolean = false;
    private _multiplierFireBallSoundPlayed: boolean = false;

    private _isAborted: boolean = false;
    set isAborted(value: boolean) {
        this._isAborted = value;
    }

    constructor() {
        super();

        this._layer = StageManager.getLayer(FITHGameConfig.instance.LAYERS.SYMBOL_WIN_ANIMATIONS.name);
    }

    public playStrokeAnimation(reelId: number, symbolId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            extraSymbol: PIXI.Sprite = this._extraSymbol[reelId][symbolId],
            commonWinAnim: TimelineSprite = this._commonWinAnim[reelId][symbolId],
            position: number[] = Helper.getSymbolPositions(reelId, symbolId, true),
            fps: number = 30,
            symbolName: string = Reels.getSymbol(reelId, symbolId)!.symName;

        if (symbolName === ReelSymbolName.WILD || symbolName === ReelSymbolName.BONUS) {
            return tl;
        }

        // setting all the elements.
        tl.add(() => {
            extraSymbol.visible = false;
            extraSymbol.texture = GameResources.getTextures(symbolName + "/" + symbolName)[0];
            extraSymbol.position.set(position[0], position[1]);
            this._layer.addChild(extraSymbol);

            commonWinAnim.hide();
            commonWinAnim.position.set(position[0], position[1]);
            this._layer.addChild(commonWinAnim);
        }, 0);

        // applying the following animations as per #8
        tl.add([
            () => extraSymbol.visible = true,
            TweenLite.to(extraSymbol, 5 / fps, {alpha: 0.8})
        ], 0);
        tl.add(commonWinAnim.getAnimationAutoShowHide(true, true), 5 / fps);
        tl.add(TweenLite.to(extraSymbol, 5 / fps, {alpha: 0}), 20 / fps);

        return tl;
    }

    public playWinAnimation(reelId: number, symbolId: number, winType: WinRatioType | undefined): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            symWinAnim: TimelineSprite = this._symWinAnim[reelId][symbolId],
            extraLSymWinAnim: TimelineSprite = this._extraLSymWinAnim[reelId][symbolId],
            symWinAnimName: string = "symWinAnim",
            position: number[] = Helper.getSymbolPositions(reelId, symbolId, true),
            fps: number = 30,
            sym: FITHReelSymbol = (Reels.getSymbol(reelId, symbolId)! as FITHReelSymbol),
            symbolName: string = sym.symName,
            symWinAnimNameSuffix: string = "_" + MathHelper.randomIntInRange(1, 2),
            extraLayerItem: TimelineSprite = this._extraLayerMedLargeWin[reelId][symbolId];

        if (symbolName === ReelSymbolName.WILD || symbolName === ReelSymbolName.BONUS) {
            return tl;
        }

        if (symbolName.indexOf("L") > -1) {
            extraLSymWinAnim.hide();
            extraLSymWinAnim.position.set(position[0], position[1]);
            this._layer.addChild(extraLSymWinAnim);
        }

        symWinAnim.hide();
        (symWinAnim as any)._textures = GameResources.getTextures(symWinAnimName + symbolName + symWinAnimNameSuffix);
        symWinAnim.position.set(position[0], position[1]);
        this._layer.addChild(symWinAnim);

        // #99
        if (winType === WinRatioType.MEDIUM || winType === WinRatioType.LARGE) {
            if (symbolName.indexOf("L") > -1) {
                (extraLayerItem as any)._textures = GameResources.getTextures("symWinAnimBigL");
            } else {
                (extraLayerItem as any)._textures = GameResources.getTextures("symWinAnimBig" + symbolName);
            }
            extraLayerItem.hide();
            extraLayerItem.position.set(position[0], position[1]);
            this._layer.addChild(extraLayerItem);
        }

        // applying the following animations as per #8
        tl.add(() => {
            sym.fadeOutSymbol();
        }, 0);
        tl.add(symWinAnim.getAnimationAutoShowHide(true, true), 5 / fps);
        if (symbolName.indexOf("L") > -1) {
            tl.add(extraLSymWinAnim.getAnimationAutoShowHide(true, true), 5 / fps);
        }
        // #99
        if (winType === WinRatioType.MEDIUM || winType === WinRatioType.LARGE) {
            tl.add(extraLayerItem.getAnimationAutoShowHide(true, true), 5 / fps);
        }

        return tl;
    }

    public playWildMiningAnimation(reelId: number, symbolId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            symWinAnimName: string = "symWinAnim",
            position: number[] = Helper.getSymbolPositions(reelId, symbolId, true),
            sym: FITHReelSymbol = (Reels.getSymbol(reelId, symbolId)! as FITHReelSymbol),
            symbolName: string = sym.symName,
            symWinAnimNameSuffix: string = "_1";

        let symWinAnim: TimelineSprite,
            extraLSymWinAnim: TimelineSprite,
            wildExplodeDSym: TimelineSprite;

        // applying the following animations as per #77
        if (symbolName.indexOf("L") > -1) {
            extraLSymWinAnim = this._extraLSymWinAnim[reelId][symbolId];
            extraLSymWinAnim.hide();
            extraLSymWinAnim.position.set(position[0], position[1]);
            this._layer.addChild(extraLSymWinAnim);

            tl.add(extraLSymWinAnim.getAnimationAutoShowHide(true, true), 0);
        } else if (symbolName.indexOf("D") > -1) {
            wildExplodeDSym = this._wildExplodeDSym[reelId][symbolId];
            wildExplodeDSym.hide();
            wildExplodeDSym.position.set(position[0], position[1]);
            this._layer.addChild(wildExplodeDSym);

            tl.add(wildExplodeDSym.getAnimationAutoShowHide(true, true), 0);
        } else {
            symWinAnim = this._symWinAnim[reelId][symbolId];
            symWinAnim.hide();
            (symWinAnim as any)._textures = GameResources.getTextures(symWinAnimName + symbolName + symWinAnimNameSuffix);
            symWinAnim.position.set(position[0], position[1]);
            this._layer.addChild(symWinAnim);

            tl.add(symWinAnim.getAnimationAutoShowHide(true, true), 0);
        }

        return tl;
    }

    public playWildExplodeAnimation(wildIds: number[], allWinPositions: boolean[][], explodePositions: boolean[][], multiplier: number, winType: WinRatioType | undefined): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            wildExplodeTl: TimelineLite = this.playWildExplode(wildIds, multiplier);

        tl.add(wildExplodeTl, 0);
        tl.add(() => {
            allWinPositions.forEach((pos: boolean[], reelId: number) => {
                pos.forEach((hasWin: boolean, symId: number) => {
                    if (hasWin) {
                        const symName: string | undefined = Reels.getSymbol(reelId, symId)?.symName;
                        if (symName && symName !== ReelSymbolName.WILD && symName !== ReelSymbolName.BONUS) {
                            this.playWinAnimation(reelId, symId, winType);
                        }
                    }
                });
            });

            explodePositions.forEach((pos: boolean[], reelId: number) => {
                pos.forEach((hasWin: boolean, symId: number) => {
                    if (hasWin && !allWinPositions[reelId][symId]) {
                        const symName: string | undefined = Reels.getSymbol(reelId, symId)?.symName;
                        if (symName && symName !== ReelSymbolName.WILD && symName !== ReelSymbolName.BONUS) {
                            this.playExplodeAnimation(reelId, symId, symName);
                            (Reels.getSymbol(reelId, symId)! as FITHReelSymbol).fadeOutSymbol();
                        }
                    }
                });
            });
        }, wildExplodeTl.totalDuration() / 2);

        // #218
        for (let i: number = 0; i < 6; i++) {
            for (let j: number = 0; j < allWinPositions[i].length; j++) {
                if (allWinPositions[i][j]){
                    const symName: string | undefined = Reels.getSymbol(i, j)?.symName;
                    if (symName && symName !== ReelSymbolName.WILD && symName !== ReelSymbolName.BONUS) {
                        tl.add(() => FITHSoundManager.playSymbolsExplodeSmall(), wildExplodeTl.totalDuration() / 2 + 8 / 30);

                        return tl;
                    }
                }
            }
        }

        return tl;
    }

    public reset(): void {
        this._commonWinAnim.forEach((anims: TimelineSprite[]) => {
            anims.forEach((anim: TimelineSprite) => {
                this._layer.removeChild(anim);
                anim.hide();
            });
        });

        this._symWinAnim.forEach((anims: TimelineSprite[]) => {
            anims.forEach((anim: TimelineSprite) => {
                this._layer.removeChild(anim);
                anim.hide();
            });
        });

        this._extraLSymWinAnim.forEach((anims: TimelineSprite[]) => {
            anims.forEach((anim: TimelineSprite) => {
                this._layer.removeChild(anim);
                anim.hide();
            });
        });

        this._extraSymbol.forEach((anims: PIXI.Sprite[]) => {
            anims.forEach((anim: PIXI.Sprite) => {
                this._layer.removeChild(anim);
                anim.visible = false;
            });
        });

        this._wildExplodeDSym.forEach((anims: TimelineSprite[]) => {
            anims.forEach((anim: TimelineSprite) => {
                this._layer.removeChild(anim);
                anim.hide();
            });
        });

        this._extraLayerMedLargeWin.forEach((anims: TimelineSprite[]) => {
            anims.forEach((anim: TimelineSprite) => {
                this._layer.removeChild(anim);
                anim.hide();
            });
        });

        this._layer.removeChildren();

        this._wildExplodeSoundPlayed = false;
        this._multiplierFireBallSoundPlayed = false;
    }

    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        const totalReels: number = FITHGameConfig.instance.REELS_NUM,
            symsIn1Reels: number = FITHGameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        for (let i: number = 0; i < totalReels; i++) {
            const commonWinAnim: TimelineSprite[] = [],
                symWinAnim: TimelineSprite[] = [],
                extraLSymWinAnim: TimelineSprite[] = [],
                extraSymbol: PIXI.Sprite[] = [],
                wildExplodeDSymAnim: TimelineSprite[] = [],
                extraLayerAnimItems: TimelineSprite[] = [];

            for (let j: number = 0; j < symsIn1Reels; j++) {
                const commonWin: TimelineSprite = new TimelineSprite(GameResources.getTextures("commonWin"));
                commonWin.blendMode = PIXI.BLEND_MODES.SCREEN;
                commonWin.anchor.set(0.5);
                commonWinAnim.push(commonWin);

                const symWin: TimelineSprite = new TimelineSprite([PIXI.Texture.EMPTY]);
                symWin.blendMode = PIXI.BLEND_MODES.SCREEN;
                symWin.anchor.set(0.5);
                symWinAnim.push(symWin);

                const extraLSymWin: TimelineSprite = new TimelineSprite(GameResources.getTextures("brokeWood"));
                extraLSymWin.anchor.set(0.5);
                extraLSymWinAnim.push(extraLSymWin);

                const extraSym: PIXI.Sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
                extraSym.blendMode = PIXI.BLEND_MODES.SCREEN;
                extraSym.anchor.set(0.5);
                extraSymbol.push(extraSym);

                const wildExplodeDSym: TimelineSprite = new TimelineSprite(GameResources.getTextures("deadExplode"));
                wildExplodeDSym.anchor.set(0.5);
                wildExplodeDSymAnim.push(wildExplodeDSym);

                const extraLayerItem: TimelineSprite = new TimelineSprite([PIXI.Texture.EMPTY]);
                extraLayerItem.blendMode = PIXI.BLEND_MODES.SCREEN;
                extraLayerItem.anchor.set(0.5);
                extraLayerAnimItems.push(extraLayerItem);
            }

            this._commonWinAnim.push(commonWinAnim);
            this._symWinAnim.push(symWinAnim);
            this._extraLSymWinAnim.push(extraLSymWinAnim);
            this._extraSymbol.push(extraSymbol);
            this._wildExplodeDSym.push(wildExplodeDSymAnim);
            this._extraLayerMedLargeWin.push(extraLayerAnimItems);
        }
    }

    private playExplodeAnimation(reelId: number, symbolId: number, symbolName: string): void {
        const position: number[] = Helper.getSymbolPositions(reelId, symbolId, true);

        let wildExplodeDSym: TimelineSprite;

        if (symbolName === ReelSymbolName.WILD || symbolName === ReelSymbolName.BONUS) {
            return;
        }

        if (symbolName.indexOf("D") > -1) {
            wildExplodeDSym = this._wildExplodeDSym[reelId][symbolId];
            wildExplodeDSym.hide();
            wildExplodeDSym.position.set(position[0], position[1]);
            this._layer.addChild(wildExplodeDSym);
            wildExplodeDSym.getAnimationAutoShowHide(true, true);
        }
    }

    private playWildExplode(wildIds: number[], multiplier: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
                onComplete: () => this.removeChild(wildAnimContainer)
            }),
            wildAnimContainer: PIXI.Container = new PIXI.Container(),
            spineName: string = "W_spine",
            animName: string = "explode";

        let wildExplodeSpine: PIXI.spine.Spine,
            wildTween: SpineTween,
            position: number[];

        for (let i: number = 0, j: number = 0; i < wildIds.length; i += 2, j++) {
            position = Helper.getSymbolPositions(wildIds[i], wildIds[i + 1], true);

            wildExplodeSpine = new PIXI.spine.Spine(GameResources.getSpineAsset(spineName));
            wildExplodeSpine.position.set(position[0], position[1]);
            wildAnimContainer.addChild(wildExplodeSpine);

            wildTween = new SpineTween(wildExplodeSpine, 0, animName);

            tl.add([
                () => {
                    // #258
                    this._layer.addChild(wildAnimContainer);

                    (Reels.getSymbol(wildIds[i], wildIds[i + 1])! as FITHReelSymbol).fadeOutSymbol();
                    FITHGameModuleConfig.coinsFountain.startWildMultiplierFountain(Helper.getSymbolPositions(wildIds[i], wildIds[i + 1], true), ++multiplier);

                    // #218
                    if (!this._isAborted || !this._multiplierFireBallSoundPlayed) {
                        this._multiplierFireBallSoundPlayed = true;
                        FITHSoundManager.playMultiplierFireBall();
                    }
                },
                wildTween
            ], (j * 0.2));
            // #146
            tl.add(() => {
                TweenLite.to(StageManager.stage, 0.2, {y: "+=" + 30, ease: "shakeBounce"});

                // #218
                if (!this._isAborted || !this._wildExplodeSoundPlayed) {
                    this._wildExplodeSoundPlayed = true;
                    FITHSoundManager.playWildExplode();
                }
            }, ((wildTween.totalDuration() / 2) + (j * 0.2)));
        }

        return tl;
    }
}